/**
 * Conversion funnel (§55).
 *
 * Every step percentage states its basis, because the stages are not one
 * population: activity stages count activities, lead stages count leads, credit
 * stages count credit rows. Comparing across a unit change produced "664%"
 * before that was measured and fixed.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { EChartsOption } from "echarts";
import { AlertTriangle, Table2 } from "lucide-react";

import { api } from "@/api/client";
import { Chart } from "@/components/charts/Chart";
import { Disclosure } from "@/components/cards/Disclosure";
import { useFilterStore } from "@/store/filters";
import type { AnalyticsEnvelope } from "@/types/api";
import { count } from "@/utils/format";

interface Stage {
  key: string;
  label: string;
  unit: string;
  count: number;
  conversion_pct: number | null;
  dropoff_pct: number | null;
  basis: string | null;
  unit_change: boolean;
  no_recent_data?: boolean;
  note?: string;
}

interface Velocity {
  transition: string;
  sample: number;
  median_days: number | null;
  p75_days: number | null;
  p90_days: number | null;
  note?: string;
}

/** Colour follows the *unit*, not the rank — a stage keeps its hue however the
 *  filters reorder the counts.
 *
 *  Validated as a categorical set against the light chart surface: lightness
 *  band, chroma floor, protan/tritan separation, normal-vision separation and
 *  contrast all pass. `credit` is #0B7A5E rather than the older #0B5D57, which
 *  failed the chroma floor at 0.072 and read as grey. */
const UNIT_COLOR: Record<string, string> = {
  activity: "#2563C9",
  lead: "#C2670A",
  credit: "#0B7A5E",
};

const UNIT_LABEL: Record<string, string> = {
  activity: "Activities",
  lead: "Lead records",
  credit: "Credit rows",
};

const UNIT_NOUN: Record<string, string> = {
  activity: "activity rows",
  lead: "lead records",
  credit: "credit rows",
};

/** Split the stage list on unit boundaries.
 *
 *  This is the whole reason there is more than one chart. A funnel's shape
 *  asserts that each band is a subset of the one above it, and across a unit
 *  change that is false — 44,614 activities do not "narrow into" 1,330 lead
 *  records, they are a different population counted a different way. Drawing
 *  one continuous funnel would restate the 664% error the backend was fixed to
 *  stop making, just in a prettier form. */
function groupByUnit(stages: Stage[]): Array<{ unit: string; stages: Stage[] }> {
  const groups: Array<{ unit: string; stages: Stage[] }> = [];
  for (const stage of stages) {
    const last = groups[groups.length - 1];
    if (last && last.unit === stage.unit) last.stages.push(stage);
    else groups.push({ unit: stage.unit, stages: [stage] });
  }
  return groups;
}

function funnelOption(unit: string, stages: Stage[]): EChartsOption {
  const colour = UNIT_COLOR[unit] ?? "#5D6E6A";
  return {
    // `trigger: item` — a funnel has no axis to point at.
    tooltip: {
      trigger: "item",
      backgroundColor: "#16211F",
      borderWidth: 0,
      textStyle: { color: "#F6F8F7", fontSize: 12 },
      formatter: (raw: unknown) => {
        const p = raw as { dataIndex: number };
        const stage = stages[p.dataIndex];
        if (!stage) return "";
        const step = stage.conversion_pct !== null
          ? `${stage.conversion_pct}% of ${stage.basis}`
          : stage.no_recent_data ? "not recorded in this period" : "base of this unit";
        return `<strong>${stage.label}</strong><br/>`
             + `${count(stage.count)} ${UNIT_NOUN[unit] ?? "rows"}<br/>`
             + `<span style="opacity:.75">${step}</span>`;
      },
    },
    series: [{
      type: "funnel",
      // Never "descending". Sorting would silently reorder Commitments above
      // Positive Responses whenever commitments exceed them — which they do,
      // because 10% of commitments carry no positive response. The declared
      // order is the business sequence and must survive.
      sort: "none",
      // Proportional to the counts, so the taper carries the drop-off rather
      // than being decorative. `min` keeps a tiny stage visible.
      funnelAlign: "center",
      left: "6%",
      right: "6%",
      top: 12,
      bottom: 12,
      minSize: "22%",
      maxSize: "100%",
      gap: 2,                       // 2px surface gap between fills
      label: {
        show: true,
        position: "inside",
        color: "#FFFFFF",
        fontSize: 12,
        fontWeight: 500,
        formatter: (raw: unknown) => {
          const p = raw as { dataIndex: number };
          const stage = stages[p.dataIndex];
          return stage ? `${stage.label}  ${count(stage.count)}` : "";
        },
      },
      labelLine: { show: false },
      itemStyle: {
        borderColor: "#FFFFFF",
        borderWidth: 2,
        borderRadius: 3,
      },
      emphasis: { label: { fontSize: 12, fontWeight: 700 } },
      data: stages.map((stage, i) => ({
        name: stage.label,
        value: Math.max(stage.count, 1),
        itemStyle: {
          color: colour,
          // Each step one shade lighter, so the sequence reads top-to-bottom
          // even where two stages are nearly the same size.
          opacity: Math.max(0.45, 1 - i * 0.16),
          // A non-nesting stage is not a subset of the one above it. Dashed
          // edge rather than a different colour, which would imply a
          // different unit.
          borderType: stage.conversion_pct === null && i > 0
            ? ("dashed" as const) : ("solid" as const),
        },
      })),
    }],
  };
}

export function FunnelPage() {
  const params = useFilterStore((s) => s.queryParams());
  const { data, isLoading } = useQuery({
    queryKey: ["funnel-page", params],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope<Record<string, number>>>(
        "/analytics/funnel", { params });
      return data;
    },
    staleTime: 300_000,
  });

  const stages = (data?.breakdown ?? []) as unknown as Stage[];
  const velocity = (data?.trend ?? []) as unknown as Velocity[];
  const groups = useMemo(() => groupByUnit(stages), [stages]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-[24px] tracking-tight">
          Conversion funnel
        </h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
          Only stages the source actually supports. A step percentage appears
          just where two stages share a unit — comparing clients against
          activities, or leads against credit rows, produces a number that looks
          precise and means nothing.
        </p>
      </div>

      {isLoading && <div className="skeleton h-80" />}

      {!isLoading && groups.length === 0 && (
        <div className="card p-10 text-center text-sm text-ink-muted">
          No stages with activity in this period.
        </div>
      )}

      {!isLoading && groups.length > 0 && (
        <div className="grid gap-4"
             style={{ gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, 300px), 1fr))` }}>
          {groups.map((group) => (
            <section key={group.unit} className="card p-4">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <h2 className="font-display font-semibold text-[15px]">
                  {UNIT_LABEL[group.unit] ?? group.unit}
                </h2>
                <span className="chip border-transparent"
                      style={{ color: UNIT_COLOR[group.unit],
                               background: `${UNIT_COLOR[group.unit]}14` }}>
                  {count(group.stages[0]!.count)} at the top
                </span>
              </div>
              <p className="text-[12px] text-ink-faint mb-1">
                Counted in {UNIT_NOUN[group.unit] ?? "rows"}.
              </p>
              <Chart option={funnelOption(group.unit, group.stages)}
                     height={64 + group.stages.length * 62} />
            </section>
          ))}
        </div>
      )}

      {!isLoading && groups.length > 1 && (
        <p className="text-[12.5px] text-ink-muted flex items-start gap-2">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-warn" />
          <span>
            One funnel per unit, deliberately. A single continuous funnel would
            claim that {count(groups[0]!.stages[0]!.count)}{" "}
            {UNIT_NOUN[groups[0]!.unit]} narrow into{" "}
            {count(groups[1]!.stages[0]!.count)} {UNIT_NOUN[groups[1]!.unit]} —
            they are different populations, and dividing one by the other yields
            a number that looks precise and means nothing.
          </span>
        </p>
      )}

      {/* The numbers behind the shapes: a funnel is read by area, which is
          approximate by nature, and every count and basis has to stay
          available exactly. */}
      {!isLoading && (
        <details className="card p-4">
          <summary className="flex items-center gap-2 cursor-pointer text-[13.5px] font-medium">
            <Table2 size={14} className="text-ink-faint" />
            Every stage as a table
          </summary>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr>
                  {["Stage", "Unit", "Count", "Step", "Basis"].map((h, i) => (
                    <th key={h} className={`label border-b border-rule px-3 py-2 ${
                      i === 2 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stages.map((stage) => (
                  <tr key={stage.key} className="border-b border-rule last:border-0">
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-sm shrink-0"
                              style={{ background: UNIT_COLOR[stage.unit] }} />
                        {stage.label}
                      </span>
                      {stage.note && (
                        <div className="flex items-start gap-1.5 text-[12px] text-warn mt-1">
                          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                          <span>{stage.note}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-ink-muted">{stage.unit}</td>
                    <td className="px-3 py-2 text-right font-mono tnum">
                      {count(stage.count)}
                    </td>
                    <td className="px-3 py-2 font-mono tnum">
                      {stage.conversion_pct !== null ? `${stage.conversion_pct}%`
                        : stage.no_recent_data ? "—" : "base"}
                    </td>
                    <td className="px-3 py-2 text-ink-faint">{stage.basis ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      <div className="card p-4">
        <h2 className="font-display font-semibold text-[17px]">Conversion velocity</h2>
        <p className="text-[12.5px] text-ink-muted mb-3">
          Median and tail, not mean — a handful of very slow conversions would
          drag an average somewhere no real case sits.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {["Transition", "Sample", "Median", "P75", "P90"].map((h, i) => (
                  <th key={h} className={`label border-b border-rule px-3 py-2 ${i ? "text-right" : "text-left"}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {velocity.map((v) => (
                <tr key={v.transition} className="border-b border-rule last:border-0">
                  <td className="px-3 py-2.5">
                    {v.transition.replace(/_/g, " ")}
                    {v.note && (
                      <div className="text-[11.5px] text-ink-faint mt-0.5">{v.note}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono tnum">{count(v.sample)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tnum">{v.median_days ?? "—"}d</td>
                  <td className="px-3 py-2.5 text-right font-mono tnum">{v.p75_days ?? "—"}d</td>
                  <td className="px-3 py-2.5 text-right font-mono tnum">{v.p90_days ?? "—"}d</td>
                </tr>
              ))}
              {velocity.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-ink-muted">
                  No attribution links yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Disclosure quality={data?.quality} metadata={data?.metadata} />
    </div>
  );
}
